import { Box, Divider, Skeleton, Stack } from "@mui/material";
import React, { memo, useMemo } from "react";
import { ComposedChart, ResponsiveContainer, XAxis } from "recharts";

import { TooltipModalButton } from "components/common/TooltipModalButton";
import { Typography } from "new-components/typography/Typography";

import { DataChart, PlotChart } from "./PlotChart";

export interface AccuracyData {
  label: string;
  plotData: DataChart;
  totalGroundTruth: number;
  totalLabeled: number;
  isMissingDataRequirements: boolean;
}

interface AccuracyDetailsProps {
  accuracyTitle: string;
  infoTooltipContent: string;
  infoTooltipTitle: string;
  XAxisTicks: number[];
  data: AccuracyData[];
}

const customizedAxisTick = (props: {
  x: number;
  y: number;
  payload: {
    value: string;
  };
}) => {
  const { x, payload } = props;
  return (
    <g transform={`translate(${x + 17},3)`}>
      <text x={0} y={0} dy={16} textAnchor="end" fill="#666">
        {payload.value}
      </text>
    </g>
  );
};

const AccuracyDetails = memo(
  ({ accuracyTitle, infoTooltipContent, infoTooltipTitle, XAxisTicks, data }: AccuracyDetailsProps) => {
    const tickLabels: Record<string, string | number>[] = useMemo(
      () =>
        XAxisTicks.map((num: number) => ({
          name: `${num}%`,
          value: num,
        })),
      [XAxisTicks]
    );

    const plotDataAvailable = useMemo(() => data.every((item) => item.plotData !== undefined), [data]);

    return (
      <>
        <Box className="flex justify-start items-center my-2">
          <Typography variant="subtitle" className="font-semibold" mr={1}>
            {accuracyTitle}
          </Typography>

          <TooltipModalButton modalTitle={infoTooltipTitle} modalContent={infoTooltipContent} color="black" />
        </Box>
        <Divider className="border-b-2" />
        <Box overflow="auto">
          <Box className="flex flex-col my-2 gap-2 mx-auto min-w-[500px] max-w-[1200px] overflow-x-auto">
            <Box className="flex">
              <Box className="w-[20%]" />
              <Box className="w-[60%]" display="flex" alignItems="end">
                <ResponsiveContainer width="100%" height={20}>
                  <ComposedChart data={tickLabels} className="[&>svg]:overflow-visible">
                    <XAxis
                      dataKey="name"
                      interval="preserveStartEnd"
                      tick={customizedAxisTick}
                      tickLine={false}
                      axisLine={false}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              <Box className="w-[20%] flex justify-between">
                <Stack gap={0.5} alignItems="center" justifyContent="center">
                  <Typography width="100%" variant="detail" align="center">
                    Ground Truth Answers
                  </Typography>

                  <Typography variant="small" align="center">
                    (Including answers from Groundlight experts.)
                  </Typography>
                </Stack>

                <Typography width="100%" variant="detail" align="center">
                  Total Human Labeled Queries
                </Typography>
              </Box>
            </Box>
            <Box className="flex flex-col gap-4 px-2 py-4 bg-white rounded-lg">
              {!plotDataAvailable &&
                data.map((item, index) => (
                  <Box className="flex" key={`${item.label}-${index + 1}`}>
                    <Box className="w-[20%] flex items-center">
                      <Typography fontWeight={index === 0 ? "bold" : "normal"}>{item.label}</Typography>
                    </Box>
                    <Box className="w-[60%]">
                      <Skeleton height="60px" />
                    </Box>
                    <Box className="w-[20%] flex justify-between items-center">
                      <Box width="100%">
                        <Skeleton height="30px" width="30px" sx={{ m: "0 auto" }} />
                      </Box>
                      <Box width="100%">
                        <Skeleton height="30px" width="30px" sx={{ m: "0 auto" }} />
                      </Box>
                    </Box>
                  </Box>
                ))}
              {plotDataAvailable &&
                data.map((item, index) => (
                  <Box className="flex" key={item.label}>
                    <Box className="w-[20%] flex items-center" pl={1}>
                      <Typography
                        fontWeight={index === 0 ? "bold" : "normal"}
                        color={item.isMissingDataRequirements ? "#C8CDD3" : undefined}
                      >
                        {item.label}
                      </Typography>
                    </Box>
                    <Box className="w-[60%]">
                      <PlotChart
                        data={item.plotData!}
                        dataTicks={XAxisTicks}
                        isMissingDataRequirements={item.isMissingDataRequirements}
                      />
                    </Box>
                    <Box
                      className="w-[20%] flex justify-between items-center"
                      color={item.isMissingDataRequirements ? "#C8CDD3" : undefined}
                    >
                      <Typography align="center" className="w-[130px]">
                        {item.totalGroundTruth}
                      </Typography>
                      <Typography align="center" className="w-[95px]">
                        {item.totalLabeled}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      </>
    );
  }
);

export default AccuracyDetails;
